import { ApolloClient, from, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";

import { SERVER } from "../config/environment";
import {
  paginationCacheHandler,
  replaceWithIncomingCacheHandler
} from "../utils/fetchHelpers";
import { rollbar } from "../loggers";
import store from "../store/configureStore";
import { setNotify } from "../actions/notifyActions";

let apolloClient = null;

// write access token to headers
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : ""
    }
  };
});

// connection to graphql server
const uploadLink = createUploadLink({
  uri: SERVER.replace("/api/", "/graphql")
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  let errMessage = "";
  let isAuthError = false;

  // Check for network errors (401 Unauthorized from server)
  if (networkError) {
    if (networkError.statusCode === 401) {
      isAuthError = true;
      console.warn('[Apollo] 401 Unauthorized - Token invalid or user logged out');
    }
  }

  // Check for GraphQL errors
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
      const format = (locations) => {
        let x = "";
        locations.forEach(
          ({ line, column }) => (x = x + `(line: ${line}, column: ${column}) `)
        );
        return x;
      };
      errMessage = errMessage.concat(
        `[GraphQL error]: Message: ${message}, Location: ${format(
          locations
        )} Path: ${path}
            `
      );

      // Check if error is authentication related
      if (
        message.includes("Unauthorized") ||
        message.includes("User must login") ||
        message.includes("Token invalidated") ||
        message.includes("Token is revoked") ||
        extensions?.code === "UNAUTHENTICATED"
      ) {
        isAuthError = true;
      }
    });

    if (!errMessage.includes("non-nullable field User.id"))
      rollbar.error(errMessage); // TODO: fix "Cannot return null for non-nullable field User.id." first
  }

  // If authentication error detected, show force relogin modal
  if (isAuthError) {
    // Clear token from localStorage
    localStorage.removeItem("token");

    // Dispatch notify action to show modal
    store.dispatch(
      setNotify(
        "Your session has expired or you have been logged out. Please login again.",
        null, // confirm callback (null = default behavior)
        null, // cancel callback
        true  // forceRelogin = true
      )
    );
  }
});

function create() {
  // Check out https://github.com/zeit/next.js/pull/4611 if you want to use the AWSAppSyncClient
  return new ApolloClient({
    link: from([errorLink, authLink, uploadLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            getSchools: paginationCacheHandler()
          }
        },
        School: {
          fields: {
            teachers: paginationCacheHandler()
          }
        },
        Translation: {
          fields: {
            meanings: replaceWithIncomingCacheHandler()
          }
        }
      }
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-first"
      }
    }
  });
}

export const initApollo = () => {
  if (!apolloClient) {
    apolloClient = create();
  }

  return apolloClient;
};
